import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Battlefield3D from "@/components/battlefield/Battlefield3D";
import HudChip from "@/components/battlefield/HudChip";
import LiveIndicator from "@/components/battlefield/LiveIndicator";
import CoinPicker from "@/components/battlefield/CoinPicker";
import RecentPairs from "@/components/battlefield/RecentPairs";
import TradeTape from "@/components/battlefield/TradeTape";
import ShareButton from "@/components/battlefield/ShareButton";
import MobileHud from "@/components/battlefield/MobileHud";
import KiaCounter from "@/components/battlefield/KiaCounter";
import MarketFeed from "@/components/battlefield/MarketFeed";
import NukeStatusHUD from "@/components/battlefield/NukeStatusHUD";
import LegendFaqPanel, { LegendFaqTrigger } from "@/components/battlefield/LegendFaqPanel";
import { BinanceStream } from "@/lib/binance";
import { pairApi } from "@/lib/pairApi";
import { useRecentPairs } from "@/hooks/useRecentPairs";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Loader2, ArrowLeftRight, RefreshCcw, AlertTriangle, Video, Sparkles, LayoutGrid } from "lucide-react";

const DEFAULT_PAIR = { symbol: "BTCUSDT", base: "BTC", quote: "USDT" };
const TRADE_BUFFER = 100;
const TITLE_THROTTLE_MS = 2000;

const fmtPrice = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtVol = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(2);
};
const fmtUsd = (n) => {
  if (n == null || Number.isNaN(n) || n <= 0) return "—";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toFixed(0);
};
const parseLevels = (raw) =>
  (raw || []).map((lvl) => ({ price: parseFloat(lvl[0]), qty: parseFloat(lvl[1]) }));
function midFromBook(bids, asks) {
  const bb = bids?.[0]?.price;
  const ba = asks?.[0]?.price;
  if (bb == null || ba == null) return null;
  return (bb + ba) / 2;
}

export default function Battlefield() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [coins, setCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [pair, setPair] = useState(DEFAULT_PAIR);
  const [pairSwitching, setPairSwitching] = useState(false);
  const [routeNotice, setRouteNotice] = useState(null);
  const [snapshotErr, setSnapshotErr] = useState(null);
  const [unknownPair, setUnknownPair] = useState(null);

  const bookRef = useRef({ bids: [], asks: [], midPrice: null });
  const tradesHistoryRef = useRef([]);
  const tradesRef = useRef([]);
  const hitsRef = useRef([]);
  const battleCanvasRef = useRef(null);
  // KIA tracking: array of {side: 'bull'|'bear', ts: performance.now()}
  const killTrackerRef = useRef([]);
  // Nuke state, published by Battlefield3D each frame, polled by NukeStatusHUD.
  const nukeStateRef = useRef({
    bullReady: true, bullCooldown: 0, bullPhase: "idle",
    bearReady: true, bearCooldown: 0, bearPhase: "idle",
  });
  // Debug: `?debug=nuke` triggers a test nuke 5s after mount.
  const isDebugNuke = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("debug") === "nuke";

  const [midPriceUi, setMidPriceUi] = useState(null);
  const [walls, setWalls] = useState({ buy: 0, sell: 0 });
  const [ticker, setTicker] = useState(null);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [tapeResetKey, setTapeResetKey] = useState(0);
  const [thinMarket, setThinMarket] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);
  // Phase 5 UI toggles — persisted in localStorage
  const [autoOrbit, setAutoOrbit] = useState(() => {
    try { return localStorage.getItem("cb_autoOrbit") === "1"; } catch { return false; }
  });
  const [hq, setHq] = useState(() => {
    try {
      const v = localStorage.getItem("cb_hq");
      // Default TRUE for Phase 7 — post-processing is the new baseline.
      if (v === null) return true;
      return v === "1";
    } catch { return true; }
  });
  const [faqOpen, setFaqOpen] = useState(false);
  const [faqPulse, setFaqPulse] = useState(false);
  useEffect(() => {
    try { localStorage.setItem("cb_autoOrbit", autoOrbit ? "1" : "0"); } catch { /* ignore */ }
  }, [autoOrbit]);
  useEffect(() => {
    try { localStorage.setItem("cb_hq", hq ? "1" : "0"); } catch { /* ignore */ }
  }, [hq]);
  useEffect(() => {
    try {
      if (localStorage.getItem("cb_hasSeenFAQ") !== "1") {
        setFaqPulse(true);
        const timer = setTimeout(() => setFaqPulse(false), 10_000);
        return () => clearTimeout(timer);
      }
    } catch { /* ignore */ }
    return undefined;
  }, []);

  const uiPriceTickRef = useRef(0);
  const wsPrevStatusRef = useRef("connecting");
  const snapshotReloadRef = useRef(0);
  const titleTickRef = useRef(0);

  const recent = useRecentPairs();

  // Coins list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await pairApi.coins();
        if (!cancelled) setCoins(data.coins || []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load coin list", { description: e.message });
      } finally {
        if (!cancelled) setCoinsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initial pair from URL
  const urlHandledRef = useRef(false);
  useEffect(() => {
    if (urlHandledRef.current) return;
    const symbolParam = searchParams.get("pair");
    const aParam = searchParams.get("a");
    const bParam = searchParams.get("b");
    urlHandledRef.current = true;
    if (symbolParam) {
      resolveAndSetBySymbol(symbolParam.toUpperCase());
    } else if (aParam && bParam) {
      resolveTwoAssets(aParam.toUpperCase(), bParam.toUpperCase());
    }
  }, []);

  // Sync URL + record recents when pair changes
  useEffect(() => {
    if (unknownPair) return;
    const current = searchParams.get("pair");
    if (current !== pair.symbol) {
      const next = new URLSearchParams(searchParams);
      next.set("pair", pair.symbol);
      next.delete("a");
      next.delete("b");
      setSearchParams(next, { replace: true });
    }
    recent.record(pair);
  }, [pair.symbol, unknownPair]);

  // Snapshot fetcher — re-invocable on reconnect / retry
  const fetchSnapshot = async (symbol, showLoading = true) => {
    if (showLoading) setPairSwitching(true);
    try {
      const data = await pairApi.snapshot(symbol);
      const bids = parseLevels(data.bids);
      const asks = parseLevels(data.asks);
      const best = midFromBook(bids, asks);
      bookRef.current = { bids, asks, midPrice: best };
      setMidPriceUi(best);
      setSnapshotErr(null);
      setThinMarket(bids.length === 0 || asks.length === 0);
      return true;
    } catch (e) {
      setSnapshotErr(e.message || "snapshot failed");
      return false;
    } finally {
      if (showLoading) setPairSwitching(false);
    }
  };

  // Load snapshot + ticker on pair change
  useEffect(() => {
    if (unknownPair) return;
    let cancelled = false;
    setTicker(null);
    setMidPriceUi(null);
    setWalls({ buy: 0, sell: 0 });
    bookRef.current = { bids: [], asks: [], midPrice: null };
    tradesHistoryRef.current = [];
    tradesRef.current = [];
    hitsRef.current = [];
    killTrackerRef.current = [];
    setTapeResetKey((k) => k + 1);

    (async () => {
      const ok = await fetchSnapshot(pair.symbol);
      if (cancelled || !ok) return;
    })();

    const loadTicker = async () => {
      try {
        const data = await pairApi.ticker(pair.symbol);
        if (cancelled) return;
        setTicker({
          lastPrice: parseFloat(data.lastPrice),
          priceChangePercent: parseFloat(data.priceChangePercent),
          quoteVolume: parseFloat(data.quoteVolume),
        });
      } catch {
        /* transient */
      }
    };
    loadTicker();
    const tickerId = setInterval(loadTicker, 10000);
    return () => {
      cancelled = true;
      clearInterval(tickerId);
    };
  }, [pair.symbol, unknownPair]);

  // WS per pair
  useEffect(() => {
    if (unknownPair) return undefined;
    const stream = new BinanceStream({
      symbol: pair.symbol,
      onStatus: (s) => setWsStatus(s),
      onDepth: (d) => {
        const bids = parseLevels(d.bids);
        const asks = parseLevels(d.asks);
        const mid = midFromBook(bids, asks);
        bookRef.current = { bids, asks, midPrice: mid };
        setThinMarket(bids.length === 0 || asks.length === 0);
        const now = performance.now();
        if (now - uiPriceTickRef.current > 200) {
          uiPriceTickRef.current = now;
          setMidPriceUi(mid);
          // Buy / sell wall totals (top-20 depth × price = notional USD).
          let buy = 0;
          let sell = 0;
          for (const l of bids) buy += l.price * l.qty;
          for (const l of asks) sell += l.price * l.qty;
          setWalls({ buy, sell });
        }
      },
      onTrade: (t) => {
        const qty = parseFloat(t.q);
        const price = parseFloat(t.p);
        const isBuyerMaker = !!t.m;
        const hist = tradesHistoryRef.current;
        hist.push(qty);
        if (hist.length > 100) hist.shift();
        let p90 = Infinity;
        let p99 = Infinity;
        if (hist.length >= 20) {
          const sorted = [...hist].sort((a, b) => a - b);
          p90 = sorted[Math.floor(sorted.length * 0.9)];
          p99 = sorted[Math.floor(sorted.length * 0.99)];
        }
        const aggressive = qty >= p90;
        const extreme = qty >= p99;
        const trade = {
          id: `${t.T || Date.now()}-${t.t || Math.random().toString(36).slice(2)}`,
          price, qty, isBuyerMaker, aggressive, timestamp: t.T || Date.now(),
        };
        tradesRef.current = [trade, ...tradesRef.current].slice(0, TRADE_BUFFER);
        // Every trade produces a combat event (not just aggressive).
        // Battlefield3D handles projectile throttling / volley batching.
        // side = losing side. m=true (buyer-maker → seller-aggressor) eats bids → bulls die.
        // m=false (buyer-aggressor) eats asks → bears die.
        const side = isBuyerMaker ? "bid" : "ask";
        hitsRef.current.push({
          side,
          severity: extreme ? 2 : aggressive ? 1 : 0,
          qty,
        });
        // Keep queue bounded — Battlefield3D drains every frame but under
        // sudden bursts we don't want the queue to grow unbounded.
        if (hitsRef.current.length > 40) {
          hitsRef.current.splice(0, hitsRef.current.length - 40);
        }
        // KIA counter is driven directly by the WS trade stream (NOT by 3D
        // projectile impacts) so it stays meaningful even on the 2D fallback
        // path (headless, GPU-disabled, old hardware, etc.).
        //   m === false → buy aggressor eats asks → BEAR died.
        //   m === true  → sell aggressor eats bids → BULL died.
        killTrackerRef.current.push({
          side: isBuyerMaker ? "bull" : "bear",
          ts: performance.now(),
        });
        if (killTrackerRef.current.length > 2000) {
          killTrackerRef.current.splice(0, killTrackerRef.current.length - 2000);
        }
      },
    });
    stream.connect();
    return () => stream.close();
  }, [pair.symbol, unknownPair]);

  // Reconnect handler: whenever WS transitions to `open` AFTER being non-open,
  // fetch a fresh snapshot (so we never render stale bids/asks) and flash a
  // "LIVE" banner briefly.
  useEffect(() => {
    const prev = wsPrevStatusRef.current;
    wsPrevStatusRef.current = wsStatus;
    if (wsStatus === "open" && (prev === "reconnecting" || prev === "closed")) {
      snapshotReloadRef.current += 1;
      fetchSnapshot(pair.symbol, false);
      setJustReconnected(true);
      const id = setTimeout(() => setJustReconnected(false), 1500);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [wsStatus, pair.symbol]);

  // Dynamic page title (throttled to 2s)
  useEffect(() => {
    const now = performance.now();
    if (now - titleTickRef.current < TITLE_THROTTLE_MS) return;
    titleTickRef.current = now;
    const price = midPriceUi ?? ticker?.lastPrice;
    const priceText = price != null && !Number.isNaN(price) ? fmtPrice(price) : "—";
    document.title = `${pair.base}/${pair.quote} · ${priceText} · Crypto Battlefield`;
  }, [midPriceUi, ticker, pair.base, pair.quote]);

  // --- Selection helpers ----------------------------------------------------
  const resolveAndSetBySymbol = async (symbol) => {
    try {
      const symInfo = await pairApi.symbols();
      const entry = symInfo.symbols.find((s) => s.symbol.toUpperCase() === symbol.toUpperCase());
      if (!entry) {
        setUnknownPair(symbol);
        return;
      }
      setUnknownPair(null);
      setRouteNotice(null);
      setPair({ symbol: entry.symbol, base: entry.base, quote: entry.quote });
    } catch (e) {
      toast.error("Symbol lookup failed", { description: e.message });
    }
  };

  const resolveTwoAssets = async (a, b) => {
    try {
      const res = await pairApi.resolve(a, b);
      if (res.direct) {
        setRouteNotice(null);
        setPair({ symbol: res.symbol, base: res.base, quote: res.quote });
      } else if (res.route === "USDT") {
        setRouteNotice({ legs: res.legs, currentIdx: 0, a, b });
        const leg = res.legs[0];
        setPair({ symbol: leg.symbol, base: leg.base, quote: leg.quote });
        toast.message(`No direct market for ${a}/${b}`, {
          description: `Showing ${leg.base}/${leg.quote} — Swap for ${res.legs[1].base}/${res.legs[1].quote}.`,
        });
      }
    } catch (e) {
      if (e.status === 404) toast.error(`No market found for ${a} and ${b}`);
      else toast.error("Pair resolution failed", { description: e.message });
    }
  };

  const onPickA = async (newA) => {
    if (!newA || newA === pair.base) return;
    if (newA === pair.quote) {
      await resolveTwoAssets(newA, pair.base);
      return;
    }
    await resolveTwoAssets(newA, pair.quote);
  };
  const onPickB = async (newB) => {
    if (!newB || newB === pair.quote) return;
    if (newB === pair.base) {
      await resolveTwoAssets(pair.quote, newB);
      return;
    }
    await resolveTwoAssets(pair.base, newB);
  };
  const swapLeg = () => {
    if (!routeNotice) return;
    const nextIdx = 1 - routeNotice.currentIdx;
    const leg = routeNotice.legs[nextIdx];
    setRouteNotice({ ...routeNotice, currentIdx: nextIdx });
    setPair({ symbol: leg.symbol, base: leg.base, quote: leg.quote });
  };
  const onPickRecent = (p) => {
    setUnknownPair(null);
    setRouteNotice(null);
    setPair({ symbol: p.symbol, base: p.base, quote: p.quote });
  };
  const resetToDefault = () => {
    setUnknownPair(null);
    setRouteNotice(null);
    setPair(DEFAULT_PAIR);
  };
  const retrySnapshot = () => {
    fetchSnapshot(pair.symbol);
  };

  const changeColor = useMemo(() => {
    if (!ticker) return "text-white";
    return ticker.priceChangePercent >= 0 ? "text-[#00FFA3]" : "text-[#FF2A2A]";
  }, [ticker]);

  // Canvas placement: on mobile leave room for the taller HUD + peek strip
  const canvasTopClass = isMobile ? "top-[38vh]" : "top-[18vh]";
  const canvasHeightClass = isMobile ? "h-[45vh]" : "h-[70vh]";

  // ==== render ====

  if (unknownPair) {
    return (
      <div
        className="relative h-screen w-screen overflow-hidden cb-page-bg text-white flex items-center justify-center p-6"
        data-testid="battlefield-page"
      >
        <div className="absolute inset-0 cb-grid-overlay pointer-events-none" />
        <div className="absolute inset-0 cb-vignette pointer-events-none" />
        <div
          className="relative z-10 cb-hud-chip max-w-md w-full p-8 flex flex-col items-center gap-4 text-center !border-[#FF2A2A]/40"
          data-testid="unknown-pair-screen"
        >
          <AlertTriangle className="w-8 h-8 text-[#FF2A2A]" />
          <div>
            <p className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-400">
              Unknown market
            </p>
            <p className="font-display text-2xl font-bold tracking-tight text-white mt-1">
              {unknownPair}
            </p>
            <p className="font-mono-tac text-[11px] text-slate-500 mt-2">
              That trading pair isn&apos;t listed on Binance.
            </p>
          </div>
          <button
            type="button"
            onClick={resetToDefault}
            className="cb-hud-chip flex items-center gap-2 px-4 py-3 min-h-[44px] hover:bg-white/10 transition-colors font-mono-tac text-[11px] uppercase tracking-[0.24em] text-white"
            data-testid="unknown-pair-back"
          >
            Back to BTC / USDT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative h-screen w-screen overflow-hidden cb-page-bg text-white"
      data-testid="battlefield-page"
    >
      <div className="absolute inset-0 cb-grid-overlay pointer-events-none" />
      <div className="absolute inset-0 cb-vignette pointer-events-none" />
      <div className="absolute inset-0 cb-noise pointer-events-none" />

      <Battlefield3D
        ref={battleCanvasRef}
        bookRef={bookRef}
        hitsRef={hitsRef}
        mobile={isMobile}
        topClass={canvasTopClass}
        heightClass={canvasHeightClass}
        autoOrbit={autoOrbit}
        hq={hq}
        debug={isDebugNuke}
        nukeStateRef={nukeStateRef}
      />

      {/* Reconnect / live-back banner (top center, non-blocking) */}
      {(wsStatus !== "open" || justReconnected) && (
        <div
          className={`fixed top-2 left-1/2 -translate-x-1/2 z-[65] cb-hud-chip px-3 py-1.5 flex items-center gap-2 pointer-events-none ${
            justReconnected
              ? "!border-[#00FFA3]/40 !bg-[#025939]/40"
              : "!border-[#FF2A2A]/40 !bg-[#590909]/40"
          }`}
          data-testid="reconnect-banner"
          data-state={justReconnected ? "reconnected" : "reconnecting"}
        >
          {justReconnected ? (
            <>
              <span className="w-2 h-2 rounded-full bg-[#00FFA3] cb-live-dot text-[#00FFA3]" />
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-white">
                Live again
              </span>
            </>
          ) : (
            <>
              <RefreshCcw className="w-3 h-3 animate-spin text-[#FF2A2A]" />
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-white">
                {wsStatus === "connecting" ? "Connecting…" : "Reconnecting…"}
              </span>
            </>
          )}
        </div>
      )}

      {isMobile ? (
        <MobileHud
          pair={pair}
          ticker={ticker}
          midPriceUi={midPriceUi}
          wsStatus={wsStatus}
          coins={coins}
          coinsLoading={coinsLoading}
          onPickA={onPickA}
          onPickB={onPickB}
          recentPairs={recent.pairs}
          onPickRecent={onPickRecent}
          onRemoveRecent={recent.remove}
          onClearRecents={recent.clear}
          routeNotice={routeNotice}
          onSwapLeg={swapLeg}
          thinMarket={thinMarket}
          canvasRef={battleCanvasRef}
          bookRef={bookRef}
          onOpenFaq={() => setFaqOpen(true)}
          faqPulse={faqPulse}
          autoOrbit={autoOrbit}
          setAutoOrbit={setAutoOrbit}
        />
      ) : (
        <DesktopHud
          pair={pair}
          ticker={ticker}
          midPriceUi={midPriceUi}
          wsStatus={wsStatus}
          coins={coins}
          coinsLoading={coinsLoading}
          onPickA={onPickA}
          onPickB={onPickB}
          recent={recent}
          onPickRecent={onPickRecent}
          routeNotice={routeNotice}
          swapLeg={swapLeg}
          fmtPrice={fmtPrice}
          fmtVol={fmtVol}
          changeColor={changeColor}
          canvasRef={battleCanvasRef}
          bookRef={bookRef}
          thinMarket={thinMarket}
          autoOrbit={autoOrbit}
          setAutoOrbit={setAutoOrbit}
          hq={hq}
          setHq={setHq}
          setFaqOpen={setFaqOpen}
          faqPulse={faqPulse}
        />
      )}

      <TradeTape tradesRef={tradesRef} pair={pair} resetKey={tapeResetKey} />

      {/* Buy / Sell wall side panels — hug the top of the battlefield territory */}
      <div
        className={`absolute z-30 pointer-events-none ${isMobile ? "left-3 top-[calc(20vh+130px)]" : "left-6 lg:left-8 top-[42vh]"}`}
        data-testid="buy-wall-panel"
      >
        <div className="cb-hud-chip !bg-black/50 backdrop-blur-md px-3 py-2 flex flex-col gap-0.5 border-l-2 !border-l-[#00FFA3]">
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400">
            Buy Wall
          </span>
          <span
            className="font-display text-base md:text-lg font-bold tabular-nums text-[#00FFA3]"
            data-testid="buy-wall-value"
          >
            {fmtUsd(walls.buy)}
          </span>
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-500">
            Top 20 bids
          </span>
        </div>
      </div>
      <div
        className={`absolute z-30 pointer-events-none ${isMobile ? "right-3 top-[calc(20vh+130px)]" : "right-[336px] lg:right-[352px] top-[42vh]"}`}
        data-testid="sell-wall-panel"
      >
        <div className="cb-hud-chip !bg-black/50 backdrop-blur-md px-3 py-2 flex flex-col gap-0.5 border-r-2 !border-r-[#FF2A2A] items-end">
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400">
            Sell Wall
          </span>
          <span
            className="font-display text-base md:text-lg font-bold tabular-nums text-[#FF2A2A]"
            data-testid="sell-wall-value"
          >
            {fmtUsd(walls.sell)}
          </span>
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-500">
            Top 20 asks
          </span>
        </div>
      </div>

      {/* Market Feed — recent aggressive trades */}
      <MarketFeed tradesRef={tradesRef} pair={pair} isMobile={isMobile} resetKey={tapeResetKey} />

      {/* Nuke status HUD */}
      <NukeStatusHUD nukeStateRef={nukeStateRef} isMobile={isMobile} />

      {/* KIA counter — bottom-left of the battlefield area */}
      <div
        className={`absolute z-30 ${isMobile ? "left-3 bottom-[calc(72px+env(safe-area-inset-bottom))]" : "left-6 lg:left-8 bottom-16"}`}
      >
        <KiaCounter
          killTrackerRef={killTrackerRef}
          resetKey={pair.symbol}
          compact={isMobile}
        />
      </div>

      {/* Legend / FAQ panel */}
      <LegendFaqPanel open={faqOpen} onOpenChange={setFaqOpen} />

      {pairSwitching && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 cb-hud-chip px-6 py-4 flex items-center gap-3"
          data-testid="pair-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin text-white" />
          <span className="font-mono-tac text-xs uppercase tracking-[0.24em] text-white">
            Loading {pair.symbol}…
          </span>
        </div>
      )}

      {snapshotErr && !pairSwitching && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 cb-hud-chip px-6 py-4 border-[#FF2A2A]/40 flex flex-col gap-3 items-center max-w-sm"
          data-testid="snapshot-error"
        >
          <div className="text-center">
            <p className="font-mono-tac text-xs uppercase tracking-[0.24em] text-[#FF2A2A]">
              Snapshot unreachable
            </p>
            <p className="font-mono-tac text-[11px] text-slate-400 mt-1 break-all">
              {snapshotErr}
            </p>
          </div>
          <button
            type="button"
            onClick={retrySnapshot}
            className="cb-hud-chip flex items-center gap-2 px-4 py-2 min-h-[44px] hover:bg-white/10 transition-colors font-mono-tac text-[11px] uppercase tracking-[0.24em] text-white"
            data-testid="snapshot-retry"
          >
            <RefreshCcw className="w-3 h-3" />
            Retry
          </button>
        </div>
      )}

      {!isMobile && (
        <footer
          className="absolute bottom-0 left-0 w-full md:right-[320px] md:w-auto z-40 flex justify-between items-end p-6 lg:p-8 pointer-events-none font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-500"
          data-testid="data-source-footer"
        >
          <span>Crypto Battlefield · Phase 7</span>
          <span>Powered by Binance public data</span>
        </footer>
      )}
      {isMobile && (
        <div
          className="absolute left-4 z-30 font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-600"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom))" }}
          data-testid="data-source-footer"
        >
          Powered by Binance public data
        </div>
      )}
    </div>
  );
}

function DesktopHud({
  pair, ticker, midPriceUi, wsStatus, coins, coinsLoading,
  onPickA, onPickB, recent, onPickRecent, routeNotice, swapLeg,
  fmtPrice, fmtVol, changeColor, canvasRef, bookRef, thinMarket,
  autoOrbit, setAutoOrbit, hq, setHq, setFaqOpen, faqPulse,
}) {
  return (
    <header
      className="absolute top-0 left-0 md:right-[320px] w-auto z-40 flex justify-between items-start gap-4 p-6 lg:p-8 pointer-events-none"
      data-testid="battlefield-hud"
    >
      <div className="flex flex-col gap-3 pointer-events-auto">
        <div className="flex flex-wrap items-center gap-3">
          <div className="cb-hud-chip px-4 py-2 flex items-center gap-3">
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-400">
              Pair
            </span>
            <span
              className="font-display text-2xl lg:text-3xl font-bold tracking-tighter"
              data-testid="pair-title"
            >
              {pair.base} <span className="text-slate-500 mx-1">/</span> {pair.quote}
            </span>
            {thinMarket && (
              <span
                className="cb-hud-chip px-2 py-0.5 font-mono-tac text-[9px] uppercase tracking-[0.24em] text-amber-400 !border-amber-400/40"
                data-testid="thin-market-label"
              >
                Thin Market
              </span>
            )}
          </div>
          <LiveIndicator status={wsStatus} />
          <ShareButton pair={pair} />
          <LegendFaqTrigger
            onClick={() => setFaqOpen(true)}
            pulse={faqPulse}
          />
          <button
            type="button"
            onClick={() => setAutoOrbit((v) => !v)}
            className={`cb-hud-chip flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors ${autoOrbit ? "!border-[#FFE480]/50 !bg-[#FFE480]/10" : ""}`}
            aria-pressed={autoOrbit}
            aria-label="Toggle cinematic auto-orbit camera"
            data-testid="orbit-toggle"
            data-state={autoOrbit ? "on" : "off"}
          >
            <Video className={`w-3.5 h-3.5 ${autoOrbit ? "text-[#FFE480]" : "text-white/80"}`} />
            <span className={`font-mono-tac text-[10px] uppercase tracking-[0.24em] ${autoOrbit ? "text-[#FFE480]" : "text-white"}`}>
              Orbit
            </span>
          </button>
          <button
            type="button"
            onClick={() => setHq((v) => !v)}
            className={`cb-hud-chip flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors ${hq ? "!border-[#FFE480]/50 !bg-[#FFE480]/10" : ""}`}
            aria-pressed={hq}
            aria-label="Toggle high-quality bloom post-processing"
            data-testid="hq-toggle"
            data-state={hq ? "on" : "off"}
          >
            <Sparkles className={`w-3.5 h-3.5 ${hq ? "text-[#FFE480]" : "text-white/80"}`} />
            <span className={`font-mono-tac text-[10px] uppercase tracking-[0.24em] ${hq ? "text-[#FFE480]" : "text-white"}`}>
              HQ
            </span>
          </button>
          <Link
            to="/warroom"
            className="cb-hud-chip flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors"
            aria-label="Open War Room multi-pair view"
            data-testid="warroom-link"
          >
            <LayoutGrid className="w-3.5 h-3.5 text-white/80" />
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.24em] text-white">
              War Room
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="coin-picker-row">
          <CoinPicker
            coins={coins}
            value={pair.base}
            disabledSymbol={pair.quote}
            onChange={onPickA}
            side="left"
            loading={coinsLoading}
            testIdPrefix="coin-picker-a"
          />
          <div className="font-display font-black text-white/70 text-xl tracking-tighter px-1">VS</div>
          <CoinPicker
            coins={coins}
            value={pair.quote}
            disabledSymbol={pair.base}
            onChange={onPickB}
            side="right"
            loading={coinsLoading}
            testIdPrefix="coin-picker-b"
          />
        </div>

        <RecentPairs
          pairs={recent.pairs}
          currentSymbol={pair.symbol}
          onPick={onPickRecent}
          onRemove={recent.remove}
          onClear={recent.clear}
        />

        <div className="flex flex-wrap gap-3">
          <HudChip label="Mid Price" value={fmtPrice(midPriceUi ?? ticker?.lastPrice)} valueClassName="text-white" testId="live-mid-price" />
          <HudChip
            label="24h Change"
            value={ticker ? `${ticker.priceChangePercent >= 0 ? "+" : ""}${ticker.priceChangePercent.toFixed(2)}%` : "—"}
            valueClassName={changeColor}
            testId="live-24h-change"
          />
          <HudChip
            label="24h Volume"
            value={ticker ? fmtVol(ticker.quoteVolume) : "—"}
            valueClassName="text-white"
            testId="live-24h-volume"
            suffix={` ${pair.quote}`}
          />
        </div>

        {routeNotice && (
          <div
            className="cb-hud-chip flex items-center gap-3 px-4 py-2 border-white/20"
            data-testid="route-notice"
          >
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-400">
              Route
            </span>
            <span className="font-mono-tac text-xs text-white">
              No direct {routeNotice.a}/{routeNotice.b} market · showing{" "}
              <span className="font-bold">{pair.base}/{pair.quote}</span>
            </span>
            <button
              type="button"
              onClick={swapLeg}
              className="ml-1 flex items-center gap-1 font-mono-tac text-[10px] uppercase tracking-[0.24em] text-[#00FFA3] hover:text-white transition-colors"
              data-testid="route-swap-leg"
            >
              <ArrowLeftRight className="w-3 h-3" />
              Swap
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 pointer-events-auto">
        <div className="cb-hud-chip px-4 py-2 flex items-center gap-3">
          <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-400">
            Theatre
          </span>
          <span className="font-display text-sm tracking-tight text-white">
            Order Book · Depth 20
          </span>
        </div>
        <div className="cb-hud-chip px-4 py-2 flex items-center gap-4">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#00FFA3] cb-live-dot text-[#00FFA3]" />
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.24em] text-slate-300">
              Bulls · {pair.base}
            </span>
          </span>
          <span className="text-slate-600">|</span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FF2A2A] cb-live-dot text-[#FF2A2A]" />
            <span className="font-mono-tac text-[10px] uppercase tracking-[0.24em] text-slate-300">
              Bears · {pair.quote}
            </span>
          </span>
        </div>
      </div>
    </header>
  );
}
